/**
 * 部屋1つの画面（issue #103・docs/01 7-9・15-1・16-1）。
 *
 * ここでできること:
 *  - 名前と設定を変える（通知に本文を載せるか・既読の見せ方・投稿できる範囲・AI先輩の参照）
 *  - 参加者を追加する／外す
 *  - 参加者を「このトークの管理者」に決める／外す（issue #516。トークの管理者は、アプリから
 *    このトークの参加者の出し入れと名前の変更ができる。1対1・お知らせのトークには決められない）
 *  - **保存期間を決める**（本部管理者のみ。「無期限 → 期限あり」は、いままで消えなかった
 *    投稿が消えるようになるため、確認をはさみ、操作履歴に残ることをお伝えします）
 *  - **「事故対応」の指定**を付け外しする（本部管理者のみ。付けた部屋は自動削除の対象外）
 *  - **削除する**（確認つき。一覧から消え、投稿できなくなりますが、中身は残り、戻せます）
 *  - 休眠中のトークなら、そのことを出し、**休眠を解く**（休眠の棚卸しの画面と同じ操作・issue #459）
 *
 * 決めごと（docs/06 決定22）:
 *  - 変えるのは「保存」を押したときだけ。押したら結果を必ず出す（原則1・2）
 *  - 消す操作の前には確認の窓をはさむ（原則4）
 *  - できる・できないを決めるのは**サーバの門番**です。この画面が行うのは
 *    **ボタンを出すか出さないか**だけで、隠していても押せてもサーバの判断が優先されます
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import type {
  ManagedChannelDetail,
  MemberPage,
  RetentionChangePreview,
  RetentionPurgeOutcome,
} from '../api/types.js';
import {
  afterEvidenceRemoved,
  COUNT_CHANGED_NEXT_STEP,
  countChangedSinceConfirm,
  EXPIRED_BACKLOG_NOTE,
  expiringReversibility,
  expiringSentence,
  losesMessages,
  purgedSentence,
  purgeNextStep,
  retentionDialogWords,
  retentionOnlyNote,
} from '../channels/retention-change.js';
import { useSession } from '../auth/session-context.js';
import { ROLE_REQUIREMENTS, useRoleWording, type RoleWording } from '../auth/role-wording.js';
import {
  channelSiteLabel,
  channelTypeLabel,
  DELETABLE_CHANNEL_TYPE,
  DIRECT_CHANNEL_TYPE,
  formatDateTime,
  postPolicyLabel,
  readReceiptLabel,
  retentionLabel,
  TEACH_CHANNEL_TYPE,
} from '../channels/labels.js';
import { ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { can } from '../menu/visible.js';
import { endsWithRequest, withReason } from '../format/sentence.js';
import { nextStepAfter } from '../api/load-failure.js';

/** 画面に出す結果のお知らせ。 */
interface Banner {
  tone: 'success' | 'error';
  message: string;
  nextStep?: string;
}

/** いま確認の窓を出している操作。 */
type Confirming = 'none' | 'delete' | 'retention' | 'evidence';

/** 参加者に追加できる方を読む上限（100名 × 50回 = 5,000名。それ以上の会社は想定していない） */
const CANDIDATE_PAGES_MAX = 50;

/**
 * 保存日数の上限（100年・サーバの受け口と同じ・issue #347）。
 * とても大きな日数を入れると、夜間の削除が「いつより前を消すか」を作れなくなるため。
 */
const MAX_RETENTION_DAYS = 36_500;

/** 保存期間の決め方（種類ごとの既定に従う／この部屋で日数を決める）。 */
type RetentionMode = 'default' | 'days';

export function ChannelDetailPage(): ReactNode {
  const api = useApi();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const { profile } = useSession();
  const grant = profile?.grant;
  // 案内に出す役職の呼び名（その会社の呼び名・issue #480）
  const wording = useRoleWording();

  const [channel, setChannel] = useState<ManagedChannelDetail | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // 名前と設定（保存を押すまで変わりません）
  const [name, setName] = useState('');
  const [postPolicy, setPostPolicy] = useState<'members' | 'admins'>('members');
  const [notifyWithBody, setNotifyWithBody] = useState(true);
  const [readReceipt, setReadReceipt] = useState<'count' | 'none'>('count');
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsResult, setSettingsResult] = useState<Banner | null>(null);

  // 保存期間
  const [retentionMode, setRetentionMode] = useState<RetentionMode>('default');
  const [retentionDays, setRetentionDays] = useState('90');
  const [retentionResult, setRetentionResult] = useState<Banner | null>(null);

  // 参加者
  const [candidates, setCandidates] = useState<MemberPage | null>(null);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [addUserId, setAddUserId] = useState('');
  const [memberResult, setMemberResult] = useState<Banner | null>(null);
  const [memberBusy, setMemberBusy] = useState(false);
  /** 「外す」の確認の窓に出している方（決定22: 外す前に確かめる・#265 L-4） */
  const [removing, setRemoving] = useState<{ userId: string; displayName: string } | null>(null);
  /** 「このトークの管理者」を決める・外すの確認の窓に出している方（issue #516・決定22） */
  const [assigning, setAssigning] = useState<{
    userId: string;
    displayName: string;
    makeAdmin: boolean;
  } | null>(null);

  const [confirming, setConfirming] = useState<Confirming>('none');
  const [busy, setBusy] = useState(false);
  const [deleteResult, setDeleteResult] = useState<Banner | null>(null);
  const [evidenceResult, setEvidenceResult] = useState<Banner | null>(null);
  /** 休眠を解く（issue #459）。何度でもやり直せる操作なので、確認の窓は出さない（休眠の棚卸しの画面と同じ） */
  const [waking, setWaking] = useState(false);
  const [wakeResult, setWakeResult] = useState<Banner | null>(null);
  /** 確認の窓に出す「いま消える件数」（保存日数を短くする・指定を外す前に数える・issue #410） */
  const [preview, setPreview] = useState<RetentionChangePreview | null>(null);

  const canChangeRetention = grant ? can(grant, 'retention.change') : false;
  const canReadRoster = grant ? can(grant, 'member.manage') : false;
  const canWake = grant ? can(grant, 'channel.manage_any') : false;
  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setChannel(null);
    setError(null);
    api
      .get<ManagedChannelDetail>(`/admin/channels/${id}`)
      .then((loaded) => {
        if (cancelled) return;
        setChannel(loaded);
        setName(loaded.name);
        setPostPolicy(loaded.post_policy);
        setNotifyWithBody(loaded.notify_with_body);
        setReadReceipt(loaded.read_receipt_display);
        setRetentionMode(loaded.retention_days_override === null ? 'default' : 'days');
        if (loaded.retention_days_override !== null) {
          setRetentionDays(String(loaded.retention_days_override));
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, id, reloadKey]);

  // 参加者に追加できる方の一覧（名簿を管理できる方だけが読めます）
  useEffect(() => {
    if (!canReadRoster) return;
    let cancelled = false;
    // 名簿は100名ずつ読むので、続きがあれば最後まで読む（101人目以降も選べるように・#265 L-3）
    const loadAll = async (): Promise<MemberPage> => {
      const items: MemberPage['items'] = [];
      let after: string | undefined;
      for (let page = 0; page < CANDIDATE_PAGES_MAX; page++) {
        const loaded = await api.get<MemberPage>('/admin/members', {
          query: { limit: 100, status: 'active', ...(after !== undefined ? { after } : {}) },
        });
        items.push(...loaded.items);
        if (loaded.nextCursor === null) return { items, nextCursor: null };
        after = loaded.nextCursor;
      }
      return { items, nextCursor: after ?? null };
    };
    loadAll()
      .then((loaded) => {
        if (!cancelled) setCandidates(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setCandidatesError(toMessage(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, canReadRoster]);

  function clearCreatedNotice(): void {
    if (!search.has('created')) return;
    setSearch(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('created');
        return next;
      },
      { replace: true },
    );
  }

  async function saveSettings(): Promise<void> {
    if (!channel) return;
    clearCreatedNotice();
    setSavingSettings(true);
    setSettingsResult(null);
    try {
      await api.patch(`/channels/${channel.id}`, {
        body: {
          name: name.trim(),
          // 1対1では「投稿できる方」を送らない（お二人とも書ける部屋・サーバも受け付けない・決定27）
          ...(channel.type === DIRECT_CHANNEL_TYPE ? {} : { post_policy: postPolicy }),
          notify_with_body: notifyWithBody,
          read_receipt_display: readReceipt,
          // 「AI先輩がこの部屋の内容を答えに使ってよい」は画面に出さず、送りもしない（決定23・issue #310）。
          // Phase 1 では部屋の会話を AI先輩の材料にしないため、入れても何も起きない設定になる。
          // 列と設定の置き場はサーバに残してあり、送らなければいまの値のまま変わらない
        },
      });
      setSettingsResult({ tone: 'success', message: '保存しました。' });
      reload();
    } catch (caught: unknown) {
      const reason = toMessage(caught);
      setSettingsResult({
        tone: 'error',
        message: withReason(`保存できませんでした`, reason),
        // 理由の文が次の一手まで言っているとき（「先に元に戻してください。」など）は足さない（#367 U-5）
        ...(endsWithRequest(reason)
          ? {}
          : { nextStep: '入力の内容をご確認のうえ、もう一度お試しください。' }),
      });
    } finally {
      setSavingSettings(false);
    }
  }

  /** 保存期間の指定（この画面の入力から、サーバへ渡す日数を決める）。 */
  function retentionValue(): number | null {
    if (retentionMode === 'default') return null;
    const days = Number(retentionDays);
    return Number.isInteger(days) && days >= 1 && days <= MAX_RETENTION_DAYS ? days : Number.NaN;
  }

  /**
   * 「無期限 → 期限あり」にあたる変更か。
   * いままで自動で消えなかった部屋に期限が付く＝**取り返しのつかない向き**の変更なので、
   * 確認の窓をはさみ、操作履歴に残ることをお伝えします（docs/01 16-1）。
   */
  function becomesLimited(): boolean {
    if (!channel) return false;
    // 「事故対応」の指定が付いている間は、日数を決めても自動では消えない（docs/01 7-1）。
    // ここで注意を出すと「消えるようになる」と読めてしまうので、指定が付いている間は出さない
    // （指定を外すときの確認で、消えるようになることをお伝えします）
    if (channel.is_evidence) return false;
    const next = retentionValue();
    return channel.retention_days === null && next !== null && !Number.isNaN(next);
  }

  /**
   * 保存日数が短くなる変更か（「無期限 → 期限あり」を含む）。短くすると、押した**その場で**
   * 期限を過ぎたメッセージと写真などのファイルが消えるので、件数つきの確認の窓をはさむ（issue #410・決定22）。
   */
  function becomesShorter(): boolean {
    if (!channel || channel.is_evidence) return false;
    const next = retentionValue();
    if (next !== null && Number.isNaN(next)) return false;
    const after = next ?? channel.retention_days_default;
    const before = channel.retention_days;
    if (after === null) return false;
    return before === null || after < before;
  }

  /**
   * 変える前に「いま消える件数」をサーバで数えて、確認の窓を開く（issue #410）。
   * 数えられなかったときは窓を開かず、理由を結果の帯に出す（件数の分からない窓で押させない）。
   */
  async function openWithPreview(
    body: { retention_days: number | null } | { is_evidence: false },
    kind: 'retention' | 'evidence',
    options: { onlyWhenLosing?: boolean } = {},
  ): Promise<void> {
    if (!channel) return;
    clearCreatedNotice();
    setBusy(true);
    setPreview(null);
    const setResult = kind === 'retention' ? setRetentionResult : setEvidenceResult;
    setResult(null);
    let savesWithoutWindow = false;
    try {
      const counted = await api.post<RetentionChangePreview>(
        `/channels/${channel.id}/retention/preview`,
        { body },
      );
      setPreview(counted);
      // 延ばす・同じ長さにする変更でも、期限を過ぎて夜間の片付けを待っているものは、その場で消える。
      // 1件でも消えるときだけ窓を出し、消えるものが無ければ窓を出さずにそのまま変える（#475 W-17・決定22）
      if (options.onlyWhenLosing && !losesMessages(counted)) savesWithoutWindow = true;
      else setConfirming(kind);
    } catch (caught: unknown) {
      setResult({
        tone: 'error',
        message: withReason(`消える件数を確かめられませんでした`, toMessage(caught)),
        nextStep: nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
      });
    } finally {
      setBusy(false);
    }
    if (savesWithoutWindow) await saveRetention(0);
  }

  /**
   * @param confirmedCount 確認の窓でお見せした「いま消える件数」（窓を出さなかったときは 0）。
   *   それより多く消えることになっていたら、サーバは何も変えずに断る（issue #410）
   */
  async function saveRetention(confirmedCount: number): Promise<void> {
    if (!channel) return;
    clearCreatedNotice();
    const next = retentionValue();
    if (Number.isNaN(next)) {
      setRetentionResult({
        tone: 'error',
        message: '保存日数は1日から36,500日（100年）までの数字でご指定ください。',
        nextStep:
          '36,500日より長く残したいときは、「事故対応」の指定をお使いください（指定の間は自動では消えません）。',
      });
      return;
    }
    setBusy(true);
    setRetentionResult(null);
    try {
      const saved = await api.patch<{ retention_purge?: RetentionPurgeOutcome }>(
        `/channels/${channel.id}`,
        { body: { retention_days: next, confirmed_expired_messages: confirmedCount } },
      );
      const purged = purgedSentence(saved?.retention_purge);
      const later = purgeNextStep(saved?.retention_purge);
      setRetentionResult({
        tone: 'success',
        message:
          (next === null
            ? '保存期間を「種類ごとの決まりに従う」にしました。'
            : `保存期間を${retentionLabel(next)}にしました。`) + purged,
        ...(later !== undefined ? { nextStep: later } : {}),
      });
      setConfirming('none');
      reload();
    } catch (caught: unknown) {
      setRetentionResult({
        tone: 'error',
        message: withReason(`保存期間を変えられませんでした`, toMessage(caught)),
        ...retentionFailureNextStep(caught, wording),
      });
      setConfirming('none');
      if (countChangedSinceConfirm(caught)) reload();
    } finally {
      setBusy(false);
    }
  }

  /** @param confirmedCount 指定を外すときに窓でお見せした「いま消える件数」（付けるときは使わない） */
  async function saveEvidence(confirmedCount: number): Promise<void> {
    if (!channel) return;
    clearCreatedNotice();
    const next = !channel.is_evidence;
    setBusy(true);
    setEvidenceResult(null);
    try {
      const saved = await api.patch<{ retention_purge?: RetentionPurgeOutcome }>(
        `/channels/${channel.id}`,
        {
          body: next
            ? { is_evidence: next }
            : { is_evidence: next, confirmed_expired_messages: confirmedCount },
        },
      );
      const later = purgeNextStep(saved?.retention_purge);
      setEvidenceResult({
        tone: 'success',
        message: next
          ? 'このトークに「事故対応」の指定を付けました。保存期間が来ても自動では消えません。'
          : '「事故対応」の指定を外しました。' +
            afterEvidenceRemoved(preview, saved?.retention_purge),
        ...(later !== undefined ? { nextStep: later } : {}),
      });
      setConfirming('none');
      reload();
    } catch (caught: unknown) {
      setEvidenceResult({
        tone: 'error',
        message: withReason(`「事故対応」の指定を変えられませんでした`, toMessage(caught)),
        ...retentionFailureNextStep(caught, wording),
      });
      setConfirming('none');
      if (countChangedSinceConfirm(caught)) reload();
    } finally {
      setBusy(false);
    }
  }

  async function addMember(): Promise<void> {
    if (!channel || addUserId === '') return;
    clearCreatedNotice();
    setMemberBusy(true);
    setMemberResult(null);
    try {
      await api.post(`/channels/${channel.id}/members`, { body: { user_ids: [addUserId] } });
      setMemberResult({ tone: 'success', message: '参加者を追加しました。' });
      setAddUserId('');
      reload();
    } catch (caught: unknown) {
      setMemberResult({
        tone: 'error',
        message: withReason(`参加者を追加できませんでした`, toMessage(caught)),
        nextStep: 'メンバーとして登録されている方かどうかをご確認ください。',
      });
    } finally {
      setMemberBusy(false);
    }
  }

  async function removeMember(userId: string, displayName: string): Promise<void> {
    if (!channel) return;
    clearCreatedNotice();
    setMemberBusy(true);
    setMemberResult(null);
    try {
      await api.del(`/channels/${channel.id}/members/${userId}`);
      setRemoving(null);
      setMemberResult({ tone: 'success', message: `${displayName}さんを外しました。` });
      reload();
    } catch (caught: unknown) {
      setRemoving(null);
      setMemberResult({
        tone: 'error',
        message: withReason(`${displayName}さんを外せませんでした`, toMessage(caught)),
        nextStep: nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
      });
    } finally {
      setMemberBusy(false);
    }
  }

  /** 「このトークの管理者」に決める・外す（issue #516）。結果は参加者の欄の帯に出す（決定22） */
  async function setChannelAdmin(
    userId: string,
    displayName: string,
    makeAdmin: boolean,
  ): Promise<void> {
    if (!channel) return;
    clearCreatedNotice();
    setMemberBusy(true);
    setMemberResult(null);
    try {
      if (makeAdmin) {
        await api.post(`/channels/${channel.id}/admins/${userId}`);
      } else {
        await api.del(`/channels/${channel.id}/admins/${userId}`);
      }
      setAssigning(null);
      setMemberResult({
        tone: 'success',
        message: makeAdmin
          ? `${displayName}さんを、このトークの管理者にしました。`
          : `${displayName}さんを、このトークの管理者から外しました。`,
      });
      reload();
    } catch (caught: unknown) {
      setAssigning(null);
      setMemberResult({
        tone: 'error',
        message: withReason(
          makeAdmin
            ? `${displayName}さんを、このトークの管理者にできませんでした`
            : `${displayName}さんを、このトークの管理者から外せませんでした`,
          toMessage(caught),
        ),
        nextStep: nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
      });
    } finally {
      setMemberBusy(false);
    }
  }

  async function wake(): Promise<void> {
    if (!channel) return;
    clearCreatedNotice();
    setWaking(true);
    setWakeResult(null);
    try {
      await api.post(`/admin/dormancy/channels/${channel.id}/wake`);
      setWakeResult({
        tone: 'success',
        message: 'このトークの休眠を解きました。',
        nextStep:
          '参加している方のトーク一覧に戻り、またメッセージを送れます（アプリでは、次にトーク一覧が新しくなったときに戻ります）。',
      });
      reload();
    } catch (caught: unknown) {
      setWakeResult({
        tone: 'error',
        message: withReason('休眠を解けませんでした', toMessage(caught)),
        nextStep: nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
      });
    } finally {
      setWaking(false);
    }
  }

  async function deleteChannel(): Promise<void> {
    if (!channel) return;
    clearCreatedNotice();
    setBusy(true);
    setDeleteResult(null);
    try {
      await api.del(`/channels/${channel.id}`);
      setConfirming('none');
      navigate('/rooms?deleted=1');
    } catch (caught: unknown) {
      setConfirming('none');
      setDeleteResult({
        tone: 'error',
        message: withReason(`削除できませんでした`, toMessage(caught)),
        nextStep: '削除できるのはグループのトークだけです。',
      });
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorBlock failure={error} onRetry={reload} />;
  if (!channel) return <LoadingBlock label="トークを読み込んでいます…" />;

  const deletable = channel.type === DELETABLE_CHANNEL_TYPE && channel.archived_at === null;
  // 削除したトークは読むだけ（#334 L-4）。押してもサーバに断られるボタンは出さず、
  // 「事故対応」の指定を外す操作だけを残す（完全に消す前に外す2段階・決定32）
  const archived = channel.archived_at !== null;
  const alreadyMember = new Set(channel.members.map((member) => member.user_id));
  // 「このトークの管理者」を決められるトークか（1対1・お知らせは決められない・削除したトークでは変えない・issue #516）
  const acceptsChannelAdmins = channel.accepts_channel_admins === true && !archived;
  /** この方を「このトークの管理者」に決める・外す操作を出すか（人だけ。無効の方は外すだけ） */
  const canAssign = (member: ManagedChannelDetail['members'][number]): boolean =>
    acceptsChannelAdmins &&
    (member.kind === undefined || member.kind === 'human') &&
    (member.is_admin === true || member.deactivated !== true);

  return (
    <article>
      <PageHeader
        title={channel.name}
        description={`${channelTypeLabel(channel.type)}／${channelSiteLabel(channel.site_name)}／参加者${channel.member_count}名`}
        actions={
          <Link className="ui-button ui-button-secondary" to="/rooms">
            一覧に戻る
          </Link>
        }
      />

      {search.get('created') === '1' ? (
        <ResultBanner
          tone="success"
          message="トークを作りました。"
          nextStep="この画面で参加者を追加し、必要であれば保存期間をご指定ください。"
        />
      ) : null}

      {channel.archived_at !== null ? (
        <ResultBanner
          tone="info"
          message="このトークは削除されています。一覧からは消え、メッセージも送れません。"
          nextStep="元に戻すときは、トークの一覧の「削除したトーク」から行ってください。"
        />
      ) : null}

      {/* 休眠中のトーク（issue #459）。一覧の「休眠中」の印と同じことを、開いた画面にも出す */}
      {channel.dormant_at != null && !archived ? (
        <div className="admin-dormant-note">
          <ResultBanner
            tone="info"
            message="このトークは休眠中です。参加している方のトーク一覧に出ず、新しいメッセージも送れません。中身は消えていません。"
            nextStep={
              canWake
                ? '休眠を解くと、参加している方のトーク一覧に戻り、またメッセージを送れます。'
                : '休眠を解くときは、トークを管理できる管理者の方にご依頼ください。'
            }
          />
          {canWake ? (
            <Button
              variant="primary"
              busy={waking}
              busyLabel="休眠を解いています…"
              onClick={() => void wake()}
            >
              休眠を解く
            </Button>
          ) : null}
        </div>
      ) : null}

      {wakeResult ? (
        <ResultBanner
          reveal
          tone={wakeResult.tone}
          message={wakeResult.message}
          {...(wakeResult.nextStep !== undefined ? { nextStep: wakeResult.nextStep } : {})}
        />
      ) : null}

      {deleteResult ? (
        <ResultBanner
          reveal
          tone={deleteResult.tone}
          message={deleteResult.message}
          {...(deleteResult.nextStep !== undefined ? { nextStep: deleteResult.nextStep } : {})}
        />
      ) : null}

      {archived ? (
        <Card title="名前と設定" description="削除したトークの設定は変えられません。">
          <dl className="admin-facts">
            <dt>名前</dt>
            <dd>{channel.name}</dd>
            {channel.type === DIRECT_CHANNEL_TYPE ? null : (
              <>
                <dt>メッセージを送れる方</dt>
                <dd>{postPolicyLabel(channel.post_policy)}</dd>
              </>
            )}
            <dt>既読の見せ方</dt>
            <dd>{readReceiptLabel(channel.read_receipt_display)}</dd>
            <dt>通知に本文を載せる</dt>
            <dd>{channel.notify_with_body ? '載せる' : '載せない'}</dd>
          </dl>
        </Card>
      ) : (
        <FormCard
          title="名前と設定"
          submitLabel="保存する"
          busy={savingSettings}
          onSubmit={() => void saveSettings()}
          onCancel={() => navigate('/rooms')}
          footer={
            settingsResult ? (
              <ResultBanner
                tone={settingsResult.tone}
                message={settingsResult.message}
                {...(settingsResult.nextStep !== undefined
                  ? { nextStep: settingsResult.nextStep }
                  : {})}
              />
            ) : undefined
          }
        >
          <label className="ui-field" htmlFor="channel-name">
            <span>名前</span>
            <input
              id="channel-name"
              type="text"
              value={name}
              maxLength={100}
              onChange={(event) => setName(event.target.value)}
            />
          </label>

          {channel.type === DIRECT_CHANNEL_TYPE ? null : (
            <label className="ui-field" htmlFor="channel-post-policy">
              <span>メッセージを送れる方</span>
              <select
                id="channel-post-policy"
                value={postPolicy}
                // 「AI先輩に教える」部屋は、どなたでも投稿できる部屋（決定23・issue #256）
                disabled={channel.type === TEACH_CHANNEL_TYPE}
                aria-describedby={
                  channel.type === TEACH_CHANNEL_TYPE ? 'channel-post-policy-note' : undefined
                }
                onChange={(event) => setPostPolicy(event.target.value as 'members' | 'admins')}
              >
                <option value="members">{postPolicyLabel('members')}</option>
                <option value="admins">{postPolicyLabel('admins')}</option>
              </select>
              {channel.type === TEACH_CHANNEL_TYPE ? (
                <small id="channel-post-policy-note">
                  「AI先輩に教える」トークは、どなたでもメッセージを送れるトークです。ここは変えられません。
                </small>
              ) : null}
            </label>
          )}

          <label className="ui-field" htmlFor="channel-read-receipt">
            <span>既読の見せ方</span>
            <select
              id="channel-read-receipt"
              value={readReceipt}
              onChange={(event) => setReadReceipt(event.target.value as 'count' | 'none')}
            >
              <option value="count">{readReceiptLabel('count')}</option>
              <option value="none">{readReceiptLabel('none')}</option>
            </select>
          </label>

          <label className="ui-check" htmlFor="channel-notify-body">
            <input
              id="channel-notify-body"
              type="checkbox"
              checked={notifyWithBody}
              onChange={(event) => setNotifyWithBody(event.target.checked)}
            />
            <span>通知に本文を載せる</span>
          </label>
        </FormCard>
      )}

      <Card title="参加者" description="このトークに入っている方の一覧です。">
        {acceptsChannelAdmins ? (
          <p className="admin-note">
            「このトークの管理者」にした方は、アプリからこのトークの参加者を加えたり外したり、名前を変えたりできます。役職は変わりません。
          </p>
        ) : null}
        {memberResult ? (
          <ResultBanner
            tone={memberResult.tone}
            message={memberResult.message}
            {...(memberResult.nextStep !== undefined ? { nextStep: memberResult.nextStep } : {})}
          />
        ) : null}

        <DataTable label="参加者の一覧" minWidth="46rem">
          <thead>
            <tr>
              <th scope="col">お名前</th>
              <th scope="col">立場</th>
              <th scope="col">
                <span className="admin-visually-hidden">このトークの管理者</span>
              </th>
              <th scope="col">
                <span className="admin-visually-hidden">操作</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {channel.members.map((member) => (
              <tr key={member.user_id}>
                <th scope="row">
                  {member.display_name}
                  {/* 無効にした方は参加人数に数えない（アプリの「参加者 ◯名」と同じ・issue #421） */}
                  {member.deactivated === true ? (
                    <small className="admin-choice-detail">無効（人数に数えません）</small>
                  ) : null}
                </th>
                <td>
                  {member.role === 'owner' ? 'このトークを作った方' : '参加者'}
                  {member.is_admin === true ? (
                    // このトークの管理者（issue #516）。作った方と重なることもある
                    <small className="admin-choice-detail">このトークの管理者</small>
                  ) : null}
                </td>
                <td>
                  {canAssign(member) ? (
                    <Button
                      variant="secondary"
                      compact
                      busy={memberBusy && assigning?.userId === member.user_id}
                      disabled={memberBusy}
                      onClick={() =>
                        setAssigning({
                          userId: member.user_id,
                          displayName: member.display_name,
                          makeAdmin: member.is_admin !== true,
                        })
                      }
                    >
                      {member.is_admin === true
                        ? 'このトークの管理者から外す'
                        : 'このトークの管理者にする'}
                    </Button>
                  ) : null}
                </td>
                <td>
                  {archived ? null : member.kind !== undefined && member.kind !== 'human' ? (
                    // AI先輩・連携ボットは外せない（#334 L-3・サーバも断る）。
                    // 「AI先輩に教える」トークから外すと、答えの受け付けが止まってしまうため
                    <small>外せません</small>
                  ) : member.auto_joined === true ? (
                    // 名簿に合わせて自動で入っている方は外せない（決定31・サーバも断る）。
                    // 押せば必ず断られるボタンは出さず、理由を言葉で出す
                    <small>自動で参加</small>
                  ) : (
                    <Button
                      variant="secondary"
                      compact
                      busy={memberBusy && removing?.userId === member.user_id}
                      disabled={memberBusy}
                      onClick={() =>
                        setRemoving({ userId: member.user_id, displayName: member.display_name })
                      }
                    >
                      外す
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>

        {channel.members.some((member) => member.auto_joined === true) ? (
          <p className="admin-note">
            {channel.site_id === null
              ? '「自動で参加」の方は、メンバーとして登録されているので全社のトークに入っています。外すときは、メンバーの画面でアカウントを無効にしてください。'
              : '「自動で参加」の方は、メンバーの所属の拠点がこの拠点なので、このトークに入っています。外すときは、メンバーの画面で所属の拠点を変えるか、アカウントを無効にしてください。'}
          </p>
        ) : null}

        {archived ? null : canReadRoster ? (
          <div className="admin-row-actions">
            <label className="ui-field" htmlFor="channel-add-member">
              <span>参加者を追加する</span>
              <select
                id="channel-add-member"
                value={addUserId}
                onChange={(event) => setAddUserId(event.target.value)}
              >
                <option value="">選んでください</option>
                {candidates?.items
                  .filter((member) => !alreadyMember.has(member.id))
                  .map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.display_name}
                    </option>
                  ))}
              </select>
            </label>
            <Button
              variant="primary"
              busy={memberBusy && removing === null}
              disabled={addUserId === '' || memberBusy}
              onClick={() => void addMember()}
            >
              追加する
            </Button>
          </div>
        ) : (
          <p className="admin-note">
            {`参加者を追加するには、メンバーを管理できる役職が要ります。${wording.ask(ROLE_REQUIREMENTS.memberManage)}`}
          </p>
        )}
        {candidatesError ? (
          <ResultBanner
            tone="info"
            message={withReason(`メンバーを読み込めませんでした`, candidatesError)}
            nextStep="参加者の追加だけがお使いいただけません。画面を開き直してください。"
          />
        ) : null}
      </Card>

      <Card
        title="保存期間"
        description={`いまの決まり：${retentionLabel(channel.retention_days)}（この種類のふだんの日数は${retentionLabel(channel.retention_days_default)}）`}
      >
        {!canChangeRetention ? (
          <p className="admin-note">
            {retentionOnlyNote(wording, {
              // 全社のトーク・1対1は、受け持ちの範囲が「全社」の役職だけが変えられる（決定27）
              companyWide: channel.site_id === null || channel.type === DIRECT_CHANNEL_TYPE,
              // 「無期限」かは、サーバの門番（retention/policy.ts）と同じく実際に効いている日数で決める。
              // retention_days は「事故対応」の指定が付いていると空になるので使わない（指定が付いていても、
              // 日数が決まっている部屋なら、拠点の役職でも日数を変えられる）
              unlimited:
                (channel.retention_days_override ?? channel.retention_days_default) === null,
            })}
          </p>
        ) : (
          <>
            {archived ? null : (
              <FormCard
                bare
                submitVariant="secondary"
                submitLabel="保存期間を変える"
                submitBusyLabel="変えています…"
                busy={busy && confirming === 'none'}
                onSubmit={() => {
                  // 短くする変更は、いま消える件数を数えてから確認の窓を出す（issue #410）
                  if (becomesShorter()) {
                    void openWithPreview({ retention_days: retentionValue() }, 'retention');
                    return;
                  }
                  // 延ばす・同じ長さにする変更も、いま消える件数を数える。期限を過ぎて夜間の片付けを
                  // 待っているものが1件でもあれば確認の窓を出し、無ければそのまま変える（#475 W-17）。
                  // 入力が数字でないときは、数えずに保存の側で理由を出す
                  const next = retentionValue();
                  if (next !== null && Number.isNaN(next)) {
                    void saveRetention(0);
                    return;
                  }
                  void openWithPreview({ retention_days: next }, 'retention', {
                    onlyWhenLosing: true,
                  });
                }}
                footer={
                  retentionResult ? (
                    <ResultBanner
                      tone={retentionResult.tone}
                      message={retentionResult.message}
                      {...(retentionResult.nextStep !== undefined
                        ? { nextStep: retentionResult.nextStep }
                        : {})}
                    />
                  ) : undefined
                }
              >
                <label className="ui-field" htmlFor="channel-retention-mode">
                  <span>このトークの保存期間</span>
                  <select
                    id="channel-retention-mode"
                    value={retentionMode}
                    onChange={(event) => setRetentionMode(event.target.value as RetentionMode)}
                  >
                    <option value="default">種類ごとの決まりに従う</option>
                    <option value="days">日数を決める</option>
                  </select>
                </label>
                {retentionMode === 'days' ? (
                  <label className="ui-field" htmlFor="channel-retention-days">
                    <span>何日残すか</span>
                    <input
                      id="channel-retention-days"
                      type="number"
                      min={1}
                      value={retentionDays}
                      onChange={(event) => setRetentionDays(event.target.value)}
                    />
                  </label>
                ) : null}
                {becomesLimited() ? (
                  <ResultBanner
                    tone="info"
                    message="いままで自動では消えなかったトークに、期限が付きます。"
                    nextStep="期限を過ぎたメッセージと写真などのファイルは、変えたその場で消えます（消える件数は、変える前の確認でお知らせします）。この変更は操作履歴に残ります。"
                  />
                ) : null}
              </FormCard>
            )}

            <div className="admin-row-actions">
              <div>
                <p className="ui-card-title">「事故対応」の指定</p>
                <p className="admin-note">
                  {channel.is_evidence
                    ? 'いま指定が付いています。保存期間が来ても、このトークのメッセージは自動では消えません。'
                    : '指定は付いていません。保存期間が来ると、古いメッセージから自動で消えます。'}
                </p>
              </div>
              <Button
                variant={channel.is_evidence ? 'danger' : 'secondary'}
                busy={busy && confirming === 'none' && channel.is_evidence}
                onClick={() =>
                  // 外すと、その場で期限を過ぎたものが消えるので、件数を数えてから窓を出す（issue #410）
                  channel.is_evidence
                    ? void openWithPreview({ is_evidence: false }, 'evidence')
                    : setConfirming('evidence')
                }
              >
                {channel.is_evidence ? '指定を外す' : '指定を付ける'}
              </Button>
            </div>
            {evidenceResult ? (
              <ResultBanner
                tone={evidenceResult.tone}
                message={evidenceResult.message}
                {...(evidenceResult.nextStep !== undefined
                  ? { nextStep: evidenceResult.nextStep }
                  : {})}
              />
            ) : null}
          </>
        )}
      </Card>

      {deletable ? (
        <Card
          title="このトークを削除する"
          description="一覧から消え、メッセージを送れなくなります。中身は保存期間まで残り、あとから元に戻せます。"
        >
          <Button variant="danger" onClick={() => setConfirming('delete')}>
            削除する
          </Button>
        </Card>
      ) : null}

      <dl className="admin-facts">
        <dt>作った日時</dt>
        <dd>{formatDateTime(channel.created_at)}</dd>
        <dt>最後のメッセージ</dt>
        <dd>{formatDateTime(channel.last_message_at)}</dd>
      </dl>

      <ConfirmDialog
        open={removing !== null}
        title="参加者から外します"
        message={
          removing
            ? `${removing.displayName}さんを「${channel.name}」から外します。このトークのメッセージは、見られなくなります。`
            : ''
        }
        reversibility="役職や拠点の決まりによっては、あとから戻せないことがあります。"
        confirmLabel="外す"
        busy={memberBusy}
        onConfirm={() => {
          if (removing) void removeMember(removing.userId, removing.displayName);
        }}
        onCancel={() => setRemoving(null)}
      />

      <ConfirmDialog
        open={assigning !== null}
        title={
          assigning?.makeAdmin === false
            ? 'このトークの管理者から外します'
            : 'このトークの管理者にします'
        }
        message={
          assigning
            ? assigning.makeAdmin
              ? `「${channel.name}」で、${assigning.displayName}さんをこのトークの管理者にします。この方は、アプリからこのトークの参加者を加えたり外したり、名前を変えたりできるようになります。役職は変わりません。`
              : `「${channel.name}」で、${assigning.displayName}さんをこのトークの管理者から外します。この方は、このトークの参加者を加えたり外したり、名前を変えたりできなくなります。このトークには参加したままです。`
            : ''
        }
        reversibility={
          assigning?.makeAdmin === false
            ? 'あとから「このトークの管理者にする」で戻せます。'
            : 'あとから「このトークの管理者から外す」で戻せます。'
        }
        reversible
        tone={assigning?.makeAdmin === false ? 'danger' : 'primary'}
        confirmLabel={
          assigning?.makeAdmin === false ? 'このトークの管理者から外す' : 'このトークの管理者にする'
        }
        busy={memberBusy}
        onConfirm={() => {
          if (assigning)
            void setChannelAdmin(assigning.userId, assigning.displayName, assigning.makeAdmin);
        }}
        onCancel={() => setAssigning(null)}
      />

      <ConfirmDialog
        open={confirming === 'delete'}
        title="このトークを削除します"
        message={`「${channel.name}」が一覧から消え、メッセージを送れなくなります。`}
        reversibility="中身は保存期間まで残ります。管理者があとから元に戻せます。"
        confirmLabel="削除する"
        busy={busy}
        onConfirm={() => void deleteChannel()}
        onCancel={() => setConfirming('none')}
      />

      <ConfirmDialog
        open={confirming === 'retention' && preview !== null}
        title={retentionDialogWords(channel.retention_days, becomesShorter()).title}
        message={
          preview
            ? `「${channel.name}」の保存期間を${retentionLabel(preview.retention_days)}にします。${expiringSentence(preview)}` +
              (becomesShorter() ? '' : EXPIRED_BACKLOG_NOTE)
            : ''
        }
        reversibility={preview ? expiringReversibility(preview) : ''}
        reversible={!losesMessages(preview)}
        confirmLabel={retentionDialogWords(channel.retention_days, becomesShorter()).confirmLabel}
        busy={busy}
        onConfirm={() => void saveRetention(preview?.message_count ?? 0)}
        onCancel={() => setConfirming('none')}
      />

      <ConfirmDialog
        open={confirming === 'evidence' && (!channel.is_evidence || preview !== null)}
        title={
          channel.is_evidence ? '「事故対応」の指定を外します' : '「事故対応」の指定を付けます'
        }
        message={
          channel.is_evidence
            ? `「${channel.name}」の「事故対応」の指定を外します。${preview ? expiringSentence(preview) : ''}`
            : `「${channel.name}」は、保存期間が来ても自動では消えなくなります。`
        }
        reversibility={
          channel.is_evidence && preview
            ? expiringReversibility(preview)
            : 'この変更は操作履歴に残ります。'
        }
        reversible={!(channel.is_evidence && losesMessages(preview))}
        confirmLabel={channel.is_evidence ? '指定を外す' : '指定を付ける'}
        // 付けるのは記録を守る（先へ進める）操作なので主役の色、外すのは赤（docs/06 4-7・#448 L-12）
        tone={channel.is_evidence ? 'danger' : 'primary'}
        busy={busy}
        onConfirm={() => void saveEvidence(preview?.message_count ?? 0)}
        onCancel={() => setConfirming('none')}
      />
    </article>
  );
}

/** 失敗の理由を、運用される方が読める言葉にする。 */
function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}

/**
 * 保存期間・「事故対応」の指定の変更が断られたときの次の一手（#448 L-10）。
 * 1対1の「事故対応」の画面（DirectEvidencePage）と同じ三段に分ける。
 *  1. 確認の窓でお見せした件数から変わっていた … 読み直したので、もう一度操作していただく
 *  2. 権限が無くて断られた（403） … その操作ができる役職の方へご依頼いただく（呼び名はその会社のもの・#480）
 *  3. それ以外 … 通信の失敗などは時間をおいて、内容の誤りは開き直して確かめていただく
 * 理由の文そのものが次の一手まで言っているとき（「…してください。」）は重ねない。
 */
function retentionFailureNextStep(caught: unknown, wording: RoleWording): { nextStep?: string } {
  if (countChangedSinceConfirm(caught)) return { nextStep: COUNT_CHANGED_NEXT_STEP };
  if (caught instanceof ApiError && caught.status === 403) {
    // 頼み先は、その会社の呼び名で（issue #480）。どの変更で断られたか分からないので、全社の方を案内する
    return {
      nextStep: wording.ask(ROLE_REQUIREMENTS.orgRetention, { lead: '変更が必要なときは' }),
    };
  }
  if (endsWithRequest(toMessage(caught))) return {};
  return { nextStep: nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。') };
}
